import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import { useEffect } from 'react'

import { CacheKeys } from '../lib/cache'
import { type PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { hasPendingParse, invalidateDocumentQueries, PARSE_PENDING, PARSE_POLL_MS } from './document'

export enum SlotStatus {
  MISSING = 'MISSING',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
  VALID = 'VALID',
}

export enum VaultFolderKey {
  CARRIER = 'carrier',
  DRIVERS = 'drivers',
  EQUIPMENT = 'equipment',
}

/** The folders that list things which each hold their own file. */
export type EntityKind = VaultFolderKey.DRIVERS | VaultFolderKey.EQUIPMENT

/** What a file is of. Equipment lists two of these, each row saying which. */
export type FileKind = 'carrier' | 'drivers' | 'trucks' | 'trailers'

/** One file on a document — its own row in the slot, with the dates the parser read off it. */
export interface SlotAttachment {
  id: string
  /** What the parser called the file, else the file's own name. */
  displayName: string
  file: string | null
  fileName: string | null
  fileSize: number | null
  dateExpiration: string | null
  status: SlotStatus
}

export interface SlotDocument {
  id: string
  displayName: string
  documentType: string
  /** PENDING while the parser still has work to do; '' when it never will. */
  parseStatus: string
  dateCreated: string
  /** The first file, for a one-line read; `attachments` has them all. */
  file: string | null
  fileName: string | null
  fileSize: number | null
  attachments: SlotAttachment[]
}

export interface ComplianceSlot {
  key: string
  label: string
  description: string
  required: boolean
  /** What an upload into this slot files as — not every type that satisfies it. */
  uploadDocumentType: string
  acceptedDocumentTypes: string[]
  validityMonths: number | null
  status: SlotStatus
  dateExpiration: string | null
  documents: SlotDocument[]
}

export interface SlotFolder {
  key: string
  label: string
  status: SlotStatus
  slots: ComplianceSlot[]
}

export interface EntitySummary {
  id: string
  kind: FileKind
  name: string
  status: SlotStatus
  requiredCount: number
  onFileCount: number
}

export interface ComplianceFile extends EntitySummary {
  sections: SlotFolder[]
}

/** How many files stand at each status. */
export type StatusTally = Record<SlotStatus, number>

export interface VaultFolder {
  key: VaultFolderKey
  label: string
  status: SlotStatus
  count: number
  requiredCount: number
  onFileCount: number
  byStatus: StatusTally
}

/** Every file in the vault — the carrier's, each driver's, each vehicle's — as one number. */
export interface FleetSummary {
  status: SlotStatus
  total: number
  byStatus: StatusTally
}

export interface VaultTree {
  folders: VaultFolder[]
  summary: FleetSummary
  /** The inbox that files whatever is emailed to it. */
  intakeEmail: string
  /** Whether an ELD feeds the roster the vault chases files for. */
  eldConnected: boolean
}

const base = (orgId: string) => `/api/v1/insure/organisations/${orgId}/vault`

export function useVaultTree(orgId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.VAULT_TREE, orgId],
    queryFn: () => axios.get(`${base(orgId!)}/tree/`).then((r) => toCamelCase(r.data) as VaultTree),
    enabled: !!orgId,
  })
  return {
    folders: data?.folders ?? [],
    summary: data?.summary,
    intakeEmail: data?.intakeEmail ?? '',
    eldConnected: data?.eldConnected ?? true,
    isLoading,
  }
}

/** Fifty a page, appended by "Load more"; a new search starts the pages over. */
export function entityListQuery(orgId: string, kind: EntityKind, q = '') {
  return {
    queryKey: [CacheKeys.VAULT_FOLDER, orgId, kind, q],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      axios
        .get(`${base(orgId)}/${kind}/`, { params: { page: pageParam, ...(q ? { q } : {}) } })
        .then((r) => toCamelCase(r.data) as PaginatedResults<EntitySummary>),
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResults<EntitySummary>, allPages: unknown[]) =>
      lastPage.next ? allPages.length + 1 : undefined,
  }
}

/** Warms the first page so opening a folder does not blank the tree you clicked from. */
export function prefetchEntityList(queryClient: QueryClient, orgId: string, kind: EntityKind) {
  return queryClient.ensureInfiniteQueryData(entityListQuery(orgId, kind))
}

/** One page of a kind's rows, for a table that pages rather than grows. */
export function useVaultEntityPage(
  orgId: string | undefined,
  kind: EntityKind,
  page: number,
  pageSize: number
) {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [CacheKeys.VAULT_FOLDER, orgId, kind, page, pageSize],
    queryFn: () =>
      axios
        .get(`${base(orgId!)}/${kind}/`, { params: { page, page_size: pageSize } })
        .then((r) => toCamelCase(r.data) as PaginatedResults<EntitySummary>),
    enabled: !!orgId,
    placeholderData: (previous) => previous,
  })
  return {
    entities: data?.results ?? [],
    total: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: page > 1,
    isLoading,
    isFetching,
  }
}

export function useVaultEntities(orgId: string | undefined, kind: EntityKind, q = '') {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    ...entityListQuery(orgId!, kind, q),
    enabled: !!orgId,
  })
  return {
    entities: data?.pages.flatMap((page) => page.results) ?? [],
    total: data?.pages[0]?.count ?? 0,
    isLoading,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  }
}

const FILE_PATH: Record<FileKind, (entityId?: string) => string> = {
  carrier: () => 'carrier/',
  drivers: (id) => `drivers/${id!}/`,
  trucks: (id) => `equipment/trucks/${id!}/`,
  trailers: (id) => `equipment/trailers/${id!}/`,
}

/** Where a file lives inside the vault; the carrier's has no id — there is exactly one. */
export function filePath(kind: FileKind, entityId?: string): string {
  if (kind === 'carrier') return 'carrier'
  if (kind === 'drivers') return `drivers/${entityId!}`
  return `equipment/${kind}/${entityId!}`
}

/** One dated document on the expiry timeline: whose, which slot, when it lapses. */
export interface TimelineItem {
  fileKind: FileKind
  subjectId: string | null
  subjectName: string
  slotKey: string
  slotLabel: string
  documentType: string
  documentId: string
  displayName: string
  dateExpiration: string | null
  status: SlotStatus
}

export interface TimelineFilters {
  fileKind?: FileKind
  slotKey?: string
}

type TimelinePage = PaginatedResults<TimelineItem> & { types: { key: string; label: string }[] }

/** A hundred dated documents a page, filtered on the server; `types` lists every type the fleet has, whatever the filter. */
export function useExpiryTimeline(orgId: string | undefined, filters: TimelineFilters = {}) {
  const params = {
    ...(filters.fileKind ? { file_kind: filters.fileKind } : {}),
    ...(filters.slotKey ? { slot_key: filters.slotKey } : {}),
  }
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [CacheKeys.VAULT_TIMELINE, orgId, params],
    queryFn: ({ pageParam }) =>
      axios
        .get(`${base(orgId!)}/timeline/`, { params: { page: pageParam, ...params } })
        .then((r) => toCamelCase(r.data) as TimelinePage),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
    enabled: !!orgId,
  })
  return {
    items: data?.pages.flatMap((page) => page.results) ?? [],
    total: data?.pages[0]?.count ?? 0,
    types: data?.pages[0]?.types ?? [],
    isLoading,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  }
}

/** Where the audit package for a file, or the whole fleet, is served from. */
/** A whole section of the vault's front page, rather than one record inside it. */
export type PackageScope = FileKind | 'drivers' | 'equipment'

function exportPath(kind?: PackageScope, entityId?: string): string {
  if (!kind) return 'export/'
  if (!entityId) return `export/${kind}/`
  return `export/${kind}/${entityId}/`
}

/** Streams the audit package zip and hands it to the browser as a download. */
export async function downloadAuditPackage(
  orgId: string,
  kind?: PackageScope,
  entityId?: string
): Promise<void> {
  const response = await axios.get(`${base(orgId)}/${exportPath(kind, entityId)}`, { responseType: 'blob' })
  const disposition = String(response.headers['content-disposition'] ?? '')
  const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? 'compliance-package.zip'
  const url = URL.createObjectURL(response.data as Blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function complianceFileQuery(orgId: string, kind: FileKind, entityId?: string) {
  return {
    queryKey: [CacheKeys.VAULT_FILE, orgId, kind, entityId ?? null],
    queryFn: () =>
      axios
        .get(`${base(orgId)}/${FILE_PATH[kind](entityId)}`)
        .then((r) => toCamelCase(r.data) as ComplianceFile),
  }
}

/** Every document in the file, flattened — what the pending-parse poll watches. */
function filedDocuments(file: ComplianceFile | undefined): SlotDocument[] {
  return file?.sections.flatMap((section) => section.slots.flatMap((slot) => slot.documents)) ?? []
}

export function useComplianceFile(orgId: string | undefined, kind: FileKind, entityId?: string) {
  const ready = !!orgId && (kind === 'carrier' || !!entityId)
  const { data, isLoading } = useQuery({
    ...complianceFileQuery(orgId!, kind, entityId),
    enabled: ready,
    refetchInterval: (query) => (hasPendingParse(filedDocuments(query.state.data)) ? PARSE_POLL_MS : false),
  })
  return { file: data, isLoading }
}

/** Which thing an upload files against. Empty for the carrier: it lands on the org. */
export interface UploadTarget {
  driver?: string
  truck?: string
  trailer?: string
}

export function uploadTarget(kind: FileKind, entityId?: string): UploadTarget {
  if (!entityId) return {}
  if (kind === 'drivers') return { driver: entityId }
  if (kind === 'trucks') return { truck: entityId }
  if (kind === 'trailers') return { trailer: entityId }
  return {}
}

/** `replace` swaps out what the slot holds; `add` files another document beside it. */
export type SlotUploadMode = 'add' | 'replace'

export function useUploadToSlot(orgId: string, target: UploadTarget) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      file,
      documentType,
      mode = 'add',
    }: {
      file: File
      documentType: string
      mode?: SlotUploadMode
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('document_type', documentType)
      formData.append('mode', mode)
      if (target.driver) formData.append('driver', target.driver)
      if (target.truck) formData.append('truck', target.truck)
      if (target.trailer) formData.append('trailer', target.trailer)
      return axios.post(`/api/v1/insure/organisations/${orgId}/documents/`, formData)
    },
    onSuccess: () => invalidateDocumentQueries(queryClient, orgId),
  })
}

export interface MissingSlot {
  key: string
  label: string
}

/** What the vault made of one upload — the app's "Got it" once the parser has read it. */
export interface UploadReceipt {
  documentId: string
  documentType: string
  slotLabel: string
  fileKind: FileKind
  subjectId: string | null
  subjectName: string
  parseStatus: string
  readAs: string | null
  readAsLabel: string | null
  /** null until the parser has read the file, or when it was not sure. */
  typeMatches: boolean | null
  /** A different roster member the document seems to name. */
  otherSubject: string | null
  dateExpiration: string | null
  slotStatus: SlotStatus
  missing: MissingSlot[]
}

const PARSE_SETTLED = new Set(['DONE', 'FAILED', 'SKIPPED'])

/** Whether the parser is done with a receipt's document, however it went. */
export function receiptSettled(receipt: UploadReceipt | undefined): receipt is UploadReceipt {
  return !!receipt && receipt.parseStatus !== PARSE_PENDING && PARSE_SETTLED.has(receipt.parseStatus)
}

/** A drop on the vault landing: banked at once, read out of band. */
export enum VaultUploadStatus {
  REVIEWING = 'reviewing',
  DONE = 'done',
}

export interface VaultUploadState {
  status: VaultUploadStatus
  /** Compliance documents the drop became — poll each one's receipt for where it landed. */
  filed: string[]
  unreadable: string[]
  rejected: string[]
}

export function useDropOnVault(orgId: string) {
  return useMutation({
    mutationFn: (files: File[]) => {
      const formData = new FormData()
      for (const file of files) formData.append('files', file)
      return axios
        .post(`${base(orgId)}/upload/`, formData)
        .then((r) => toCamelCase(r.data) as VaultUploadState & { sourceId: string })
    },
  })
}

/** Polls one drop until the parser has been through every file on it. */
export function useVaultUploadState(orgId: string | undefined, sourceId: string | null) {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: [CacheKeys.VAULT_UPLOAD, orgId, sourceId],
    queryFn: () =>
      axios.get(`${base(orgId!)}/upload/${sourceId!}/`).then((r) => toCamelCase(r.data) as VaultUploadState),
    enabled: !!orgId && !!sourceId,
    refetchInterval: (query) => (query.state.data?.status === VaultUploadStatus.DONE ? false : PARSE_POLL_MS),
  })
  // The filed documents are new rows in the tree and the folders, not just a toast.
  useEffect(() => {
    if (data?.status === VaultUploadStatus.DONE && orgId) invalidateDocumentQueries(queryClient, orgId)
  }, [data?.status, orgId, queryClient])
  return { state: data }
}

export function useUploadReceipt(orgId: string | undefined, documentId: string | null) {
  const { data } = useQuery({
    queryKey: [CacheKeys.VAULT_RECEIPT, orgId, documentId],
    queryFn: () =>
      axios.get(`${base(orgId!)}/receipt/${documentId!}/`).then((r) => toCamelCase(r.data) as UploadReceipt),
    enabled: !!orgId && !!documentId,
    // Re-ask while the parser still owes its read; the slot flips off PENDING at the same time.
    refetchInterval: (query) => (receiptSettled(query.state.data) ? false : PARSE_POLL_MS),
  })
  return { receipt: data }
}

/** The operation as the vault understands it — looked up by DOT, then confirmed by the carrier. */
export interface ScopeProfile {
  dotNumber: string
  legalName: string
  entityType: string
  operatingStatus: string
  powerUnits: number | null
  driversCount: number | null
  interstate: boolean | null
  hazmat: boolean | null
  cargo: string[]
}

/** Where Cai's record and the FMCSA lookup disagree; Cai's wins, the clash is shown. */
export interface ScopeConflict {
  field: string
  ours: unknown
  fmcsa: unknown
}

export interface ScopeDraft {
  /** Whether the fleet's DOT was found at FMCSA. */
  found: boolean
  /** Whether the fleet has a DOT at all; without one there is nothing to look up or confirm. */
  hasDot: boolean
  profile: ScopeProfile
  sources: Record<string, string>
  conflicts: ScopeConflict[]
  roster: { drivers: number; trucks: number; trailers: number }
  checklist: { perDriver: number; perVehicle: number; carrier: number; expectedItems: number; onFile: number }
  /** The business owners' emails: who the vault writes to unless the carrier says otherwise. */
  contacts: string[]
  /** Where the vault actually writes — the carrier's own list, else the contacts. */
  notificationEmails: string[]
  /** Where the vault texts; empty means it only writes. */
  notificationPhones: string[]
  /** Facts the insurance application already answered, counted once each. */
  answerCount: number
  /** Compliance documents already filed against the roster and the company file. */
  documentCount: number
  notes: string[]
  summary: string
}

export interface ComplianceScope {
  id: string
  dotNumber: string
  legalName: string
  entityType: string
  operatingStatus: string
  interstate: boolean | null
  hazmat: boolean | null
  cargo: string[]
  /** Empty means the business owners, whoever they are on the day we write. */
  notificationEmails: string[]
  notificationPhones: string[]
  dateLookedUp: string | null
  dateConfirmed: string | null
}

/** What the carrier signs off: the two answers, and where the vault should write. */
export interface ScopeFacts {
  interstate: boolean | null
  hazmat: boolean | null
  notificationEmails: string[]
  notificationPhones?: string[]
}

export interface FirstAsk {
  fileKind: FileKind
  subjectId: string | null
  name: string
  items: string[]
}

interface ScopeResponse {
  scope: ComplianceScope | null
  draft: ScopeDraft
}

/** What confirming answers with: the saved scope, the first files to send, and who heard. */
export interface ConfirmedScope {
  scope: ComplianceScope
  firstAsks: FirstAsk[]
  emailed: boolean
  /** Exactly the addresses the digest went to. */
  notified: string[]
}

export function useComplianceScope(orgId: string | undefined) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [CacheKeys.VAULT_SCOPE, orgId],
    queryFn: () => axios.get(`${base(orgId!)}/scope/`).then((r) => toCamelCase(r.data) as ScopeResponse),
    enabled: !!orgId,
  })
  // `scope: null` means no scope yet; a failed read means we do not know, which is not the same.
  return { scope: data?.scope ?? null, draft: data?.draft, isLoading, isError, refetch }
}

export function useConfirmComplianceScope(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (facts: ScopeFacts) =>
      axios
        .post(`${base(orgId)}/scope/confirm/`, toSnakeCase(facts))
        .then((r) => toCamelCase(r.data) as ConfirmedScope),
    onSuccess: (result) => {
      // The confirmed scope lands at once; the refetch behind it only refreshes the draft.
      queryClient.setQueryData<ScopeResponse | undefined>([CacheKeys.VAULT_SCOPE, orgId], (current) =>
        current ? { ...current, scope: result.scope } : current
      )
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.VAULT_SCOPE, orgId] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.VAULT_TREE, orgId] })
    },
  })
}
